/**
 * Cycles Page Component
 *
 * Main page for viewing and managing OKR cycles.
 * Includes create, edit, and delete functionality.
 * Integrated with real-time Firestore data.
 */

import React, { useState } from 'react';
import clsx from 'clsx';
import {
  PlusIcon,
} from '@heroicons/react/24/outline';
import type { CyclesProps } from './cycles.types';
import type { Cycle } from '../../models';
import CycleCard from './components/CycleCard';
import CreateCycleModal from './components/CreateCycleModal';
import { useCyclesQuery } from '../../hooks/data/useCyclesQuery';
import { useCycleMutations } from '../../hooks/data/useCycleMutations';
import { useAuth } from '@/contexts/AuthContext';

export default function Cycles({ className }: CyclesProps) {

  const { user } = useAuth();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingCycle, setEditingCycle] = useState<Cycle | null>(null);

  // Real-time query for cycles
  const { cycles, isLoading, error } = useCyclesQuery();

  // Mutations
  const { createCycle, updateCycle, deleteCycle } = useCycleMutations();

  const handleCreateCycle = async (data: any) => {
    if (!user) return;

    try {
      if (editingCycle) {
        await updateCycle.mutateAsync({
          id: editingCycle.id,
          data: {
            ...data,
          },
        });
      } else {
        await createCycle.mutateAsync({
          ...data,
          createdBy: user.uid,
        });
      }
      handleCloseModal();
    } catch (err) {
      console.error('Error saving cycle:', err);
      // TODO: Show error toast
    }
  };

  const handleEditCycle = (cycle: Cycle) => {
    setEditingCycle(cycle);
    setIsCreateModalOpen(true);
  };

  const handleDeleteCycle = async (cycleId: string) => {
    const cycle = cycles.find((c) => c.id === cycleId);

    const confirmMessage = `Tem certeza que deseja excluir o ciclo "${cycle?.name}"?\n\nEsta ação não pode ser desfeita.\n\nDeseja continuar?`;

    if (!confirm(confirmMessage)) return;

    try {
      await deleteCycle.mutateAsync(cycleId);
    } catch (err) {
      console.error('Error deleting cycle:', err);
      // Toast is shown by the mutation's onError handler
    }
  };

  const handleCloseModal = () => {
    setIsCreateModalOpen(false);
    setEditingCycle(null);
  };

  // Sort cycles by start date (most recent first)
  const sortedCycles = [...cycles].sort((a, b) => {
    return b.startDate.toMillis() - a.startDate.toMillis();
  });

  // Loading state
  if (isLoading && cycles.length === 0) {
    return (
      <div className={clsx('p-6', className)}>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-blue-600"></div>
            <p className="mt-4 font-montserrat text-gray-600">Carregando ciclos...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={clsx('p-6', className)}>
        <div className="rounded-lg border-2 border-red-200 bg-red-50 p-6 text-center">
          <p className="font-montserrat text-lg text-red-800">
            Erro ao carregar ciclos
          </p>
          <p className="mt-2 text-sm text-red-600">
            {error instanceof Error ? error.message : 'Erro desconhecido'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={clsx('space-y-6 p-6', className)}>
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="font-montserrat text-3xl font-bold text-gray-900">
            Ciclos OKR
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Gerencie os períodos de avaliação dos objetivos
          </p>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 font-montserrat font-medium text-white shadow-sm transition-colors hover:bg-blue-700"
        >
          <PlusIcon className="h-5 w-5" />
          Criar Ciclo
        </button>
      </div>

      {/* All Cycles */}
      {sortedCycles.length > 0 && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
          {sortedCycles.map((cycle) => (
            <CycleCard
              key={cycle.id}
              cycle={cycle}
              onEdit={handleEditCycle}
              onDelete={handleDeleteCycle}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {cycles.length === 0 && !isLoading && (
        <div className="rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
          <p className="font-montserrat text-lg text-gray-500">
            Nenhum ciclo cadastrado.
          </p>
          <p className="mt-2 text-sm text-gray-400">
            Crie seu primeiro ciclo para começar a gerenciar OKRs.
          </p>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="mt-4 rounded-lg bg-blue-600 px-6 py-2 font-montserrat font-medium text-white transition-colors hover:bg-blue-700"
          >
            Criar Primeiro Ciclo
          </button>
        </div>
      )}

      {/* Create/Edit Modal */}
      <CreateCycleModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleCreateCycle}
        editingCycle={editingCycle}
      />
    </div>
  );
}
